import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Transition } from '@headlessui/react';
import { useVideoImportContext } from '../VideoImportContext';
import { VIDEO_IMPORT_STEPS } from './constants';
import { classnames } from '@/libs/utils';
import Button from '@/components/atoms/Button/Button';
import useContentUpload from '@/hooks/useContentUpload';
import { ContentStatesEnum } from '@/Pages/VideoImportPage/constants';
import { initContent, USER_NOT_ADMIN_OR_IN_ORG_ERROR_MESSAGE } from '@/stores/core';
import { useAppContext } from '@/context/AppContext/AppContext';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';

const VideoImportPageStepsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(VIDEO_IMPORT_STEPS[0]);

  const { broadcastId, eventId } = useParams<{ eventId: string; broadcastId: string }>();
  const navigate = useNavigate();
  const { logger } = useAppContext();
  const { setFilters, navigationDisabled } = useVideoImportContext();

  const { getContentUploadById, contentUploadList, initUploadedContent } = useContentUpload();
  const contentUpload = getContentUploadById(broadcastId);
  const { contentState } = contentUpload;

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.UploadContentStarted, ({ projectId, contentId }) => {
      navigate(`/import/${projectId}/${contentId}`, { replace: true });
      setCurrentStep(VIDEO_IMPORT_STEPS[1]);
    });
    return () => {
      EventBus.off(CustomEvents.UploadContentStarted, eventListener);
    };
  }, []);

  useEffect(() => {
    const isFreshImport = !broadcastId || !eventId;

    if (isFreshImport) {
      setLoading(false);
      return;
    }

    const isInvalidImport =
      !contentUpload.projectId ||
      !contentUpload.contentId ||
      contentUpload.projectId !== eventId ||
      contentUpload.contentId !== broadcastId;

    if (isInvalidImport) {
      setCurrentStep(VIDEO_IMPORT_STEPS[0]);
      setLoading(false);
      return;
    }

    if (contentState === ContentStatesEnum.Generating) {
      setCurrentStep(VIDEO_IMPORT_STEPS[VIDEO_IMPORT_STEPS.length - 1]);
    } else if (contentState === ContentStatesEnum.ContentReadyForPreview) {
      navigate(`/${eventId}/${broadcastId}/clips`, { replace: true });
    }

    setLoading(false);
  }, [broadcastId, eventId, contentState, contentUpload.projectId, contentUpload.contentId]);

  useEffect(() => {
    if (!broadcastId || !eventId) {
      return;
    }

    if (
      currentStep.id === VIDEO_IMPORT_STEPS[0].id &&
      (contentState === ContentStatesEnum.Processing || contentState === ContentStatesEnum.Uploading)
    ) {
      setCurrentStep(VIDEO_IMPORT_STEPS[1]);
    }
  }, [contentState, currentStep.id, broadcastId, eventId]);

  useEffect(() => {
    if (!broadcastId || !eventId || contentUploadList[broadcastId]) {
      return;
    }

    initContent(broadcastId)
      .then(res => {
        const contentDetails = initUploadedContent(res);
        if (contentDetails.contentState === ContentStatesEnum.ContentReadyForPreview) {
          navigate(`/${eventId}/${broadcastId}/clips`, { replace: true });
        } else {
          // Handle the case where the user may have already submitted customizations
          setCurrentStep(VIDEO_IMPORT_STEPS[1]);
        }
      })
      .catch(err => {
        const isUserNotInOrgOrNotAdmin =
          err.statusCode === 404 && err.message === USER_NOT_ADMIN_OR_IN_ORG_ERROR_MESSAGE;
        const isAbortError = err.name === 'AbortError';

        if (!(isUserNotInOrgOrNotAdmin || isAbortError)) {
          logger.error('Failed to fetch uploaded content', err);
        }
        navigate('/');
      })
      .finally(() => {
        setLoading(false);
      });
    return;
  }, [broadcastId, eventId, contentUploadList, initUploadedContent]);

  const onSkip = useCallback(() => {
    if (currentStep.id === 'branding-select') {
      setCurrentStep(VIDEO_IMPORT_STEPS[VIDEO_IMPORT_STEPS.length - 1]);
    }
  }, [currentStep]);

  const onNext = useCallback(() => {
    if (currentStep.index === VIDEO_IMPORT_STEPS.length - 1) {
      return;
    }
    setCurrentStep(prev => VIDEO_IMPORT_STEPS[prev.index + 1]);
    setFilters({ size: undefined, title: undefined });
  }, [currentStep.index, setFilters]);

  const onPrev = useCallback(() => {
    if (currentStep.index === 0) {
      return;
    }
    setCurrentStep(prev => VIDEO_IMPORT_STEPS[prev.index - 1]);
    setFilters({ size: undefined, title: undefined });
  }, [currentStep.index, setFilters]);

  const showPreviousButton = useMemo(() => {
    if (currentStep.id === 'template-select') {
      return true;
    }

    if (
      currentStep.id === 'done' &&
      [ContentStatesEnum.Uploading, ContentStatesEnum.Processing].includes(contentState)
    ) {
      return true;
    }

    return false;
  }, [currentStep, contentState]);

  return (
    <div
      className={classnames({
        'relative flex size-full items-center justify-center overflow-hidden': true,
        'bg-[rgba(250,250,252,0.92)] backdrop-blur-[20px] backdrop-saturate-[180%]':
          currentStep.id === 'template-select'
      })}
    >
      <div
        className={classnames({
          'bottom-1/2 translate-y-1/2': currentStep.id === 'content-upload',
          'bottom-0 translate-y-[-20%]': currentStep.id === 'branding-select' || currentStep.id === 'done',
          'bottom-0 translate-y-0': currentStep.id === 'template-select',
          'pointer-events-auto absolute transition-all duration-[750ms] ease-in-out': true
        })}
      >
        <Transition
          show={!loading}
          appear
          enter="transition-all duration-750"
          enterFrom="opacity-0 translate-y-4"
          enterTo="opacity-100 translate-y-0"
        >
          <div
            className={classnames({
              'h-[33rem] w-[50rem] rounded-2xl border-slate-200': ['done', 'content-upload'].includes(currentStep.id),
              'h-[80vh] max-h-screen w-screen rounded-t-2xl border-transparent': currentStep.id === 'template-select',
              'h-[29.5rem] w-[50rem] rounded-2xl border-slate-200': currentStep.id === 'branding-select',
              'relative z-20 flex translate-y-2 flex-col border bg-[rgba(250,250,252,0.92)]': true,
              'p-6 pb-4 backdrop-blur-[20px] backdrop-saturate-[180%] transition-all duration-[750ms] ease-in-out': true
            })}
          >
            <div className="mb-4 flex items-center justify-between">
              <div className="flex w-1/3 items-center space-x-2">
                <h2 className="text-2xl font-semibold">{currentStep.title}</h2>
              </div>
              <div className="flex w-1/3 justify-center">
                {currentStep.id !== 'done' && (
                  <p className="inline-flex text-sm tabular-nums text-slate-500">
                    {currentStep.index + 1} / {VIDEO_IMPORT_STEPS.length}
                  </p>
                )}
              </div>
              <div className="flex w-1/3 justify-end">
                {currentStep.id !== 'done' && (
                  <div className="h-2 w-32 overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-slate-800 transition-all duration-200 ease-in-out"
                      style={{ width: `${((currentStep.index + 1) / VIDEO_IMPORT_STEPS.length) * 100}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
            <p className="mb-5 text-sm text-slate-600">{currentStep.description}</p>
            {currentStep.index + 1 <= VIDEO_IMPORT_STEPS.length && (
              <>
                <div
                  className={classnames({
                    'overflow-y-scroll rounded-2xl bg-white': currentStep.id === 'template-select',
                    'p-1': currentStep.id !== 'template-select',
                    'flex grow flex-col': true
                  })}
                >
                  <currentStep.component />
                </div>
                {currentStep.id !== 'content-upload' && (
                  <div
                    className={classnames({
                      'border-t border-t-slate-300': currentStep.id === 'template-select',
                      'flex justify-between pt-5': true
                    })}
                  >
                    {showPreviousButton ? (
                      <Button
                        trackingId="back-button"
                        onClick={onPrev}
                        variation="outline"
                        buttonSize="large"
                        className="bg-white"
                        title="Previous"
                        disabled={navigationDisabled}
                      >
                        Previous
                      </Button>
                    ) : (
                      <div />
                    )}
                    <div className="flex gap-2">
                      {currentStep.id === 'branding-select' && (
                        <Button
                          trackingId="skip-button"
                          onClick={onSkip}
                          variation="outline"
                          buttonSize="large"
                          className="bg-white"
                          title="Skip"
                          disabled={navigationDisabled}
                        >
                          Skip
                        </Button>
                      )}
                      {currentStep.id !== 'done' ? (
                        <Button
                          trackingId="next-button"
                          onClick={onNext}
                          variation="filled"
                          buttonSize="large"
                          title="Next"
                          disabled={navigationDisabled}
                        >
                          Next
                        </Button>
                      ) : (
                        <div />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Transition>
      </div>
    </div>
  );
};

export default memo(VideoImportPageStepsPanel);
